import React, { useState, useEffect } from 'react';
import './styles/main.css';
import { getUser, isLoggedIn, logout } from './api';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import AbsenPage from './pages/AbsenPage';
import JadwalPage from './pages/JadwalPage';
import { LaporanPage } from './pages/LaporanPage';
import { RequestPage, AdminPage, NotifPage } from './pages/others';
import { ProfilPage } from './pages/ProfilPage';

// SVG Icons
const Icons = {
  home: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  absen: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
  jadwal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  laporan: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  request: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  admin: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M6 20v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/></svg>,
  notif: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  profil: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
};

// Nav config per role
const navConfig = {
  admin: [
    { id: 'dashboard', label: 'Beranda', icon: Icons.home },
    { id: 'absen', label: 'Absensi', icon: Icons.absen },
    { id: 'laporan', label: 'Laporan', icon: Icons.laporan },
    { id: 'request', label: 'Request', icon: Icons.request },
    { id: 'admin', label: 'Kelola', icon: Icons.admin },
  ],
  spv: [
    { id: 'dashboard', label: 'Beranda', icon: Icons.home },
    { id: 'absen', label: 'Absensi', icon: Icons.absen },
    { id: 'laporan', label: 'Laporan', icon: Icons.laporan },
    { id: 'request', label: 'Request', icon: Icons.request },
    { id: 'profil', label: 'Profil', icon: Icons.profil },
  ],
  anggota: [
    { id: 'dashboard', label: 'Beranda', icon: Icons.home },
    { id: 'absen', label: 'Absen', icon: Icons.absen },
    { id: 'jadwal', label: 'Jadwal', icon: Icons.jadwal },
    { id: 'laporan', label: 'Laporan', icon: Icons.laporan },
    { id: 'profil', label: 'Profil', icon: Icons.profil },
  ],
  client: [
    { id: 'dashboard', label: 'Beranda', icon: Icons.home },
    { id: 'request', label: 'Request', icon: Icons.request },
    { id: 'notif', label: 'Notifikasi', icon: Icons.notif },
    { id: 'profil', label: 'Profil', icon: Icons.profil },
  ],
};

function App() {
  const [user, setUser] = useState(null);
  const [activePage, setActivePage] = useState('dashboard');
  const [showLanding, setShowLanding] = useState(true);
  const [activePageTab, setActivePageTab] = useState('');
  const [notifCount, setNotifCount] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBtn, setShowInstallBtn] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBtn(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setShowInstallBtn(false);
    setInstallPrompt(null);
  };

  const navigateTo = (page, tab = '') => {
    setActivePage(page);
    setActivePageTab(tab);
  };

  // Ref untuk track activePage (hindari stale closure)
  const activePageRef = React.useRef(activePage);
  useEffect(() => { activePageRef.current = activePage; }, [activePage]);

  // Double back to exit di PWA
  const backPressRef = React.useRef(0);
  const backTimerRef = React.useRef(null);
  const toastRef = React.useRef(null);

  useEffect(() => {
    const handleBack = (e) => {
      window.history.pushState(null, '', window.location.href);

      if (activePageRef.current !== 'dashboard') {
        setActivePage('dashboard');
        setActivePageTab('');
        backPressRef.current = 0;
        return;
      }

      backPressRef.current++;
      if (backPressRef.current === 1) {
        // Toast "Tekan back lagi untuk keluar"
        toastRef.current = document.createElement('div');
        toastRef.current.innerText = 'Tekan back sekali lagi untuk keluar';
        toastRef.current.style.cssText = 'position:fixed;bottom:90px;left:50%;transform:translateX(-50%);background:rgba(30,45,69,0.95);color:#f0f4f8;padding:12px 20px;border-radius:8px;font-size:0.85rem;z-index:9999;white-space:nowrap;border:1px solid rgba(201,168,76,0.3);font-family:Inter,sans-serif';
        document.body.appendChild(toastRef.current);
        backTimerRef.current = setTimeout(() => {
          backPressRef.current = 0;
          if (toastRef.current && toastRef.current.parentNode) {
            toastRef.current.parentNode.removeChild(toastRef.current);
            toastRef.current = null;
          }
        }, 2500);
      } else if (backPressRef.current >= 2) {
        clearTimeout(backTimerRef.current);
        window.history.go(-2);
      }
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handleBack);
    return () => window.removeEventListener('popstate', handleBack);
  }, []);

  useEffect(() => {
    if (isLoggedIn()) {
      setUser(getUser());
    }
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setActivePage('dashboard');
  };

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard': return <Dashboard user={user} setActivePage={navigateTo} />;
      case 'absen': return <AbsenPage user={user} />;
      case 'jadwal': return <JadwalPage user={user} />;
      case 'laporan': return <LaporanPage user={user} />;
      case 'request': return <RequestPage user={user} />;
      case 'admin': return <AdminPage user={user} defaultTab={activePageTab} />;
      case 'notif': return <NotifPage user={user} />;
      case 'profil': return <ProfilPage user={user} setUser={setUser} />;
      default: return <Dashboard user={user} setActivePage={setActivePage} />;
    }
  };

  if (!user && showLanding) return <LandingPage onGoToLogin={() => setShowLanding(false)} />;
  if (!user) return <LoginPage onLogin={handleLogin} onInstall={showInstallBtn ? handleInstall : null} onBack={() => setShowLanding(true)} />;

  const navItems = navConfig[user.role] || navConfig.anggota;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top Header */}
      <header className="top-header">
        <div className="header-brand">
          <img src="/logo-mig.png" alt="MIG" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <div>
            <div className="header-brand-name"><span>MIG</span> Security</div>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn btn-icon btn-ghost" onClick={() => setActivePage('notif')} style={{ position: 'relative' }}>
            {Icons.notif}
            {notifCount > 0 && (
              <span style={{ position: 'absolute', top: 6, right: 6, background: 'var(--red)', color: 'white', fontSize: '0.6rem', fontWeight: 700, width: 14, height: 14, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {notifCount > 9 ? '9+' : notifCount}
              </span>
            )}
          </button>
          <button className="btn btn-icon btn-ghost" onClick={() => setActivePage('profil')}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, var(--navy-500), var(--gold-glow))', border: '1px solid var(--border-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: 'var(--gold-400)' }}>
              {user.name?.charAt(0).toUpperCase()}
            </div>
          </button>
        </div>
      </header>

      {/* Page Content */}
      <main style={{ flex: 1 }}>
        {renderPage()}
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        {navItems.map(item => (
          <button
            key={item.id}
            className={`bottom-nav-item ${activePage === item.id ? 'active' : ''}`}
            onClick={() => { setActivePage(item.id); setActivePageTab(''); }}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

export default App;
